import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import { Notification } from 'types/notification';
import * as notificationApi from 'api/notificationApi';
import {
  listSelectors,
  listActions,
} from 'store/reducers/notificationReducer/listSlice';

/**
 * お知らせ一覧 store の実装をコンポーネントから隠すための中間層
 * @returns お知らせ一覧 store の selector のラッパーと action dispatch 処理 のラッパー
 */
export const useListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(listSelectors.loading);
  const currentPage = useAppSelector(listSelectors.currentPage);
  const lastPage = useAppSelector(listSelectors.lastPage);
  const notifications = useAppSelector(listSelectors.notifications);
  const selectedNotification = useAppSelector(
    listSelectors.selectedNotification,
  );
  const finalUpdateTime = useAppSelector(listSelectors.finalUpdateTime);

  const fetchNotifications = useCallback(
    async (page: number) => {
      dispatch(listActions.loadStarted());
      const result = await notificationApi.fetchNotifications(page);
      if (result.ok) {
        dispatch(
          listActions.fetchNotificationsSuccess({
            apiResponse: result.value,
            fetchedAt: new Date().toISOString(),
          }),
        );
      } else {
        toast({
          status: 'error',
          description: `お知らせ一覧の取得に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(listActions.loadEnded());
    },
    [dispatch, toast],
  );

  const selectNotification = useCallback(
    (notification: Notification) => {
      dispatch(listActions.notificationSelected({ notification }));
    },
    [dispatch],
  );

  const clearSelectNotification = useCallback(() => {
    dispatch(listActions.selectedNotificationCleared());
  }, [dispatch]);

  return {
    loading,
    currentPage,
    lastPage,
    notifications,
    selectedNotification,
    finalUpdateTime,
    fetchNotifications,
    selectNotification,
    clearSelectNotification,
  };
};
